import Plugin from '../core/Plugin';

/**
 * ColumnPlugin - Handles column layout support (newspaper-style columns)
 */
class ColumnPlugin extends Plugin {
  constructor(editor, options = {}) {
    super(editor, options);
    this.name = 'Column';
  }

  getToolbarButtons() {
    return [
      {
        name: 'columns',
        buttons: [
          { command: 'setOneColumn', label: '1 Col', title: 'One Column (Remove Columns)' },
          { command: 'setTwoColumns', label: '2 Col', title: 'Two Columns' },
          { command: 'setThreeColumns', label: '3 Col', title: 'Three Columns' },
        ],
      },
    ];
  }

  executeCommand(command) {
    switch (command) {
      case 'setOneColumn':
        this.setColumnCount(1);
        break;
      case 'setTwoColumns':
        this.setColumnCount(2);
        break;
      case 'setThreeColumns':
        this.setColumnCount(3);
        break;
    }
  }

  /**
   * Set the column count for the editor content
   * @param {number} columnCount - Number of columns (1, 2, or 3)
   */
  setColumnCount(columnCount) {
    if (this.editor.mode !== 'rich') return;

    const richEditor = this.editor.richEditor;
    richEditor.focus({ preventScroll: true });

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      // No selection - apply to entire editor content
      this.applyColumnsToEditor(columnCount);
      this.editor.syncToTextarea();
      this.editor.saveToHistory();
      return;
    }

    const range = selection.getRangeAt(0);

    // Check if we're inside an existing column container
    let columnContainer = this.findColumnContainer(range.commonAncestorContainer);

    if (columnContainer) {
      // Update existing column container
      this.updateColumnContainer(columnContainer, columnCount);
    } else {
      // Create new column container for selection
      this.wrapSelectionInColumns(range, columnCount);
    }

    this.editor.syncToTextarea();
    this.editor.saveToHistory();
  }

  /**
   * Apply column layout to the entire editor
   * @param {number} columnCount - Number of columns
   */
  applyColumnsToEditor(columnCount) {
    const richEditor = this.editor.richEditor;

    // Check if editor content is already wrapped in a column container
    const existingContainer = richEditor.querySelector('.quillkit-column-container');

    if (existingContainer) {
      // Update existing container
      this.updateColumnContainer(existingContainer, columnCount);
    } else {
      // Wrap all content in a column container
      const content = richEditor.innerHTML;
      richEditor.innerHTML = '';

      if (columnCount === 1) {
        // Just restore content without columns
        richEditor.innerHTML = content;
      } else {
        // Create column container with explicit column divs
        const container = this.createColumnContainer(columnCount);
        // Put all content in the first column by default
        const columns = container.querySelectorAll('.quillkit-column');
        if (columns.length > 0) {
          columns[0].innerHTML = content;
        }
        richEditor.appendChild(container);
      }
    }
  }

  /**
   * Wrap selected content in a column container
   * @param {Range} range - Selection range
   * @param {number} columnCount - Number of columns
   */
  wrapSelectionInColumns(range, columnCount) {
    if (range.collapsed) return;

    if (columnCount === 1) {
      // Remove columns by unwrapping
      const container = this.findColumnContainer(range.commonAncestorContainer);
      if (container) {
        this.unwrapColumnContainer(container);
      }
      return;
    }

    const container = this.createColumnContainer(columnCount);

    try {
      // Extract selected content
      const contents = range.extractContents();

      // Get all child nodes from the extracted content
      const nodes = Array.from(contents.childNodes);

      // Split nodes across columns
      const columns = container.querySelectorAll('.quillkit-column');
      if (columns.length > 0 && nodes.length > 0) {
        // Distribute nodes evenly across columns
        const nodesPerColumn = Math.ceil(nodes.length / columnCount);

        for (let i = 0; i < columnCount; i++) {
          const startIdx = i * nodesPerColumn;
          const endIdx = Math.min(startIdx + nodesPerColumn, nodes.length);
          const columnNodes = nodes.slice(startIdx, endIdx);

          // Add nodes to this column
          columnNodes.forEach((node) => {
            columns[i].appendChild(node);
          });

          // If column is empty after distribution, ensure it has a placeholder
          if (columnNodes.length === 0 && columns[i].innerHTML !== '<p><br></p>') {
            columns[i].innerHTML = '<p><br></p>';
          }
        }
      }

      // Insert the column container
      range.insertNode(container);

      // Clear selection
      window.getSelection().removeAllRanges();
    } catch (e) {
      console.error('Error wrapping selection in columns:', e);
    }
  }

  /**
   * Create a column container element with explicit column divs
   * @param {number} columnCount - Number of columns
   * @returns {HTMLElement} The column container element
   */
  createColumnContainer(columnCount) {
    const container = document.createElement('div');
    container.className = 'quillkit-column-container';
    container.setAttribute('data-column-count', columnCount);

    // Create explicit column divs
    for (let i = 0; i < columnCount; i++) {
      const column = document.createElement('div');
      column.className = 'quillkit-column';
      column.setAttribute('data-column-index', i);
      // Add a placeholder to empty columns so they're visible and editable
      if (i > 0) {
        column.innerHTML = '<p><br></p>';
      }
      container.appendChild(column);
    }

    return container;
  }

  /**
   * Apply column styles to a container
   * @param {HTMLElement} container - The container element
   * @param {number} columnCount - Number of columns
   */
  applyColumnStyles(container, columnCount) {
    if (columnCount === 1) {
      container.style.columnCount = '';
      container.style.columnGap = '';
      container.style.columnRule = '';
    } else {
      container.style.columnCount = columnCount.toString();
      container.style.columnGap = '20px';
      container.style.columnRule = '1px solid #ddd';
    }
  }

  /**
   * Update an existing column container
   * @param {HTMLElement} container - The existing container
   * @param {number} columnCount - New column count
   */
  updateColumnContainer(container, columnCount) {
    if (columnCount === 1) {
      // Remove columns
      this.unwrapColumnContainer(container);
    } else {
      const currentCount = container.querySelectorAll('.quillkit-column').length;

      if (currentCount === columnCount) {
        // No change needed
        return;
      }

      container.setAttribute('data-column-count', columnCount);

      if (currentCount < columnCount) {
        // Add more columns
        for (let i = currentCount; i < columnCount; i++) {
          const column = document.createElement('div');
          column.className = 'quillkit-column';
          column.setAttribute('data-column-index', i);
          column.innerHTML = '<p><br></p>';
          container.appendChild(column);
        }
      } else {
        // Remove excess columns (preserving content from removed columns)
        const columns = Array.from(container.querySelectorAll('.quillkit-column'));
        const contentToPreserve = [];

        for (let i = columnCount; i < currentCount; i++) {
          const columnToRemove = columns[i];
          // Save content from columns being removed
          if (columnToRemove.innerHTML.trim() && columnToRemove.innerHTML !== '<p><br></p>') {
            contentToPreserve.push(columnToRemove.innerHTML);
          }
          columnToRemove.remove();
        }

        // Append preserved content to the last remaining column
        if (contentToPreserve.length > 0 && columnCount > 0) {
          const lastColumn = container.querySelector('.quillkit-column:last-child');
          if (lastColumn) {
            contentToPreserve.forEach((content) => {
              lastColumn.insertAdjacentHTML('beforeend', content);
            });
          }
        }
      }
    }
  }

  /**
   * Unwrap a column container, restoring normal layout
   * @param {HTMLElement} container - The container to unwrap
   */
  unwrapColumnContainer(container) {
    const parent = container.parentNode;
    if (!parent) return;

    // Collect content from all columns
    const columns = container.querySelectorAll('.quillkit-column');
    columns.forEach((column) => {
      while (column.firstChild) {
        parent.insertBefore(column.firstChild, container);
      }
    });

    // Remove the container
    parent.removeChild(container);
  }

  /**
   * Find a column container in the DOM hierarchy
   * @param {Node} node - Starting node
   * @returns {HTMLElement|null} The column container or null
   */
  findColumnContainer(node) {
    let current = node;

    while (current && current !== this.editor.richEditor) {
      if (
        current.nodeType === Node.ELEMENT_NODE &&
        current.classList &&
        current.classList.contains('quillkit-column-container')
      ) {
        return current;
      }
      current = current.parentNode;
    }

    return null;
  }

  getMetadata() {
    return {
      name: 'Column Layout',
      version: '1.0.0',
      description: 'Provides multi-column layout support (newspaper-style columns)',
      author: 'AtlaraFirm',
    };
  }
}

export default ColumnPlugin;
